import React, { useState, useEffect } from "react";
import { FaUser, FaCheck } from "react-icons/fa";
import { FormField, ToggleCard } from "../../UI/DesignStandards";
import { InlineLookup } from "./InlineLookup";
import { CatalogMatchList } from "./CatalogMatchList";
import { SidePotSelection } from "./SidePotSelection";
import BowlerBracketTickets from "../../Brackets/BowlerBracketTickets";

const addOptionalFieldsLabel = (names) => {
  if (names.length === 1) return `Add ${names[0]}`;
  if (names.length === 2) return `Add ${names[0]} or ${names[1]}`;
  if (names.length === 3) return `Add ${names[0]}, ${names[1]}, or ${names[2]}`;
  return "";
};

export const BowlerCard = ({
  bowler,
  index,
  onBowlerUpdate,
  onBowlerBlur,
  sidePotEvents,
  onSidePotToggle,
  bracketProducts = [],
  onTicketChange,
  onSinglesToggle,
  showSinglesToggle = false,
  singlesEvent = null,
  onLookupToggle,
  onLookupSearch,
  onLookupSelect,
  onCatalogSelect,
  onCatalogClear,
  catalogName = "",
  existingBowlers = [],
  errors = {},
  nameInputRef = null,
  allEventConfigs = [],
  selectedEvent = null,
  getAllEventStatus = null,
}) => {
  const [searchTerm, setSearchTerm] = useState(bowler.name || "");
  const [selectedLookupIndex, setSelectedLookupIndex] = useState(-1);
  const [showOptionalDetails, setShowOptionalDetails] = useState(false);

  const hasUsbc = Boolean(String(bowler.usbcID || "").trim());
  const hasEmail = Boolean(String(bowler.email || "").trim());
  const hasPhone = Boolean(String(bowler.phone || "").trim());
  const showUsbcField = hasUsbc || showOptionalDetails;
  const showEmailField = hasEmail || showOptionalDetails;
  const showPhoneField = hasPhone || showOptionalDetails;
  const hiddenOptionalNames = [
    !hasUsbc ? "USBC ID" : null,
    !hasEmail ? "Email" : null,
    !hasPhone ? "Phone" : null,
  ].filter(Boolean);

  useEffect(() => {
    setShowOptionalDetails(false);
  }, [bowler.id]);

  // Keep searchTerm in sync with bowler.name so the search bar always matches
  useEffect(() => {
    setSearchTerm(bowler.name || "");
  }, [bowler.name]);

  const handleNameChange = (value) => {
    onBowlerUpdate(index, "name", value);
    setSearchTerm(value);

    // Trigger lookup search if there's a value
    if (value.trim() && onLookupSearch) {
      onLookupSearch(index, value);
    }
  };

  const handleAverageChange = (value) => {
    // Only allow numbers, round to integer
    const numericValue = value.replace(/[^0-9]/g, "");
    // Round to integer if there's a value
    const intValue =
      numericValue === "" ? "" : Math.round(parseFloat(numericValue) || 0);
    onBowlerUpdate(index, "average", intValue);
  };

  const handleAverageBlur = (value) => {
    if (onBowlerBlur) {
      onBowlerBlur(index, "average", value);
    }
  };

  const handleLookupSelect = (selectedBowler) => {
    onLookupSelect(index, selectedBowler);
    setSearchTerm(selectedBowler.name);
    setSelectedLookupIndex(-1);
  };

  const handleNameKeyDown = (e) => {
    if (!bowler.lookupResults || bowler.lookupResults.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedLookupIndex((prev) =>
          prev < bowler.lookupResults.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedLookupIndex((prev) =>
          prev > 0 ? prev - 1 : bowler.lookupResults.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (
          selectedLookupIndex >= 0 &&
          selectedLookupIndex < bowler.lookupResults.length
        ) {
          handleLookupSelect(bowler.lookupResults[selectedLookupIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setSelectedLookupIndex(-1);
        break;
    }
  };

  const handleLookupToggle = () => {
    onLookupToggle(index);
    if (!bowler.isLookupOpen) {
      setSearchTerm(bowler.name);
    }
  };

  return (
    <div className="card-inset p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <FaUser className="text-brand-muted" />
          <span className="font-display font-bold text-brand-charcoal">
            Bowler {index + 1}
          </span>
        </div>

        {/* Existing Bowler Indicator */}
        {bowler.existingBowler && (
          <div className="flex items-center space-x-1 text-xs border border-brand-charcoal/20 text-brand-charcoal px-2 py-1 rounded-lg">
            <FaCheck className="text-brand-charcoal" />
            <span>Existing Bowler</span>
          </div>
        )}
      </div>

      {/* Name Input with Lookup */}
      <div className="space-y-2">
        <FormField
          label="Name"
          type="text"
          value={bowler.name}
          onChange={(e) => handleNameChange(e.target.value)}
          onKeyDown={handleNameKeyDown}
          onBlur={(e) =>
            onBowlerBlur && onBowlerBlur(index, "name", e.target.value)
          }
          error={errors[`bowler_${index}_name`]}
          placeholder="Enter bowler name"
          required
          maxLength={50}
          ref={nameInputRef}
        />

        {/* Inline Lookup Dropdown - Always show when there are results */}
        {bowler.lookupResults && bowler.lookupResults.length > 0 && (
          <InlineLookup
            isOpen={true}
            searchTerm={searchTerm}
            results={bowler.lookupResults}
            selectedIndex={selectedLookupIndex}
            onSelect={handleLookupSelect}
          />
        )}
      </div>

      {/* Average Input */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-brand-charcoal">
            Average
            {bowler.hasAverage ? (
              <span className="text-red-500"> *</span>
            ) : null}
          </label>
          <label className="flex items-center space-x-2 text-sm text-brand-muted">
            <input
              type="checkbox"
              checked={!bowler.hasAverage}
              onChange={(e) => {
                const hasAverage = !e.target.checked;
                onBowlerUpdate(index, "hasAverage", hasAverage);
                if (!hasAverage) {
                  onBowlerUpdate(index, "average", "");
                }
              }}
              className="rounded border-brand-charcoal/20 text-brand-charcoal focus:ring-brand-gold-ink"
            />
            <span>No Average</span>
          </label>
        </div>

        {bowler.hasAverage ? (
          <div className="space-y-1">
            <FormField
              label=""
              type="number"
              value={bowler.average}
              onChange={(e) => handleAverageChange(e.target.value)}
              onBlur={(e) => handleAverageBlur(e.target.value)}
              onWheel={(e) => {
                // Prevent scroll from changing the value
                e.target.blur();
              }}
              error={errors[`bowler_${index}_average`]}
              placeholder="Enter average (0-300)"
              min="0"
              max="300"
              step="1"
            />

            {/* Auto-populated indicator */}
            {bowler.existingBowler &&
              bowler.averagePopulated &&
              !bowler.catalogMatch && (
              <p className="text-xs text-brand-muted flex items-center space-x-1">
                <FaCheck className="text-brand-muted" />
                <span>Average auto-populated from existing bowler data</span>
              </p>
            )}
          </div>
        ) : (
          <div className="card-inset px-3 py-2 text-sm text-brand-muted">
            Bowler has no established average
          </div>
        )}

        {bowler.usbcConflict ? (
          <p className="text-xs text-brand-charcoal">
            USBC ID on the sheet does not match this bowler. The org USBC ID was
            kept.
          </p>
        ) : null}

        {(bowler.catalogMatch ||
          (!(bowler.lookupResults && bowler.lookupResults.length > 0) &&
            bowler.catalogSuggestions &&
            bowler.catalogSuggestions.length > 0)) && (
            <CatalogMatchList
              catalogName={catalogName}
              suggestions={bowler.catalogSuggestions || []}
              selected={bowler.catalogMatch}
              enteredAverage={
                bowler.hasAverage === false ? null : bowler.average
              }
              hasPreviousAverage={
                Boolean(bowler.averageDisagrees) ||
                !!(
                  bowler.existingBowler &&
                  bowler.previousHasAverage !== undefined
                )
              }
              onSelect={(match) =>
                onCatalogSelect && onCatalogSelect(index, match)
              }
              onClear={
                onCatalogClear &&
                bowler.averagePopulated &&
                !bowler.averageDisagrees
                  ? () => onCatalogClear(index)
                  : undefined
              }
            />
          )}
      </div>

      {(showUsbcField || showEmailField) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {showUsbcField ? (
            <FormField
              label="USBC ID"
              type="text"
              value={bowler.usbcID || ""}
              onChange={(e) => {
                onBowlerUpdate(index, "usbcID", e.target.value);
                if (onLookupSearch) {
                  onLookupSearch(index, bowler.name);
                }
              }}
              onBlur={(e) =>
                onBowlerBlur && onBowlerBlur(index, "usbcID", e.target.value)
              }
              error={errors[`bowler_${index}_usbcID`]}
              placeholder="Enter USBC ID"
              maxLength={20}
            />
          ) : null}
          {showEmailField ? (
            <FormField
              label="Email"
              type="email"
              value={bowler.email || ""}
              onChange={(e) => onBowlerUpdate(index, "email", e.target.value)}
              onBlur={(e) =>
                onBowlerBlur && onBowlerBlur(index, "email", e.target.value)
              }
              error={errors[`bowler_${index}_email`]}
              placeholder="Enter email address"
              maxLength={100}
            />
          ) : null}
        </div>
      )}

      {showPhoneField ? (
        <FormField
          label="Phone"
          type="tel"
          value={bowler.phone || ""}
          onChange={(e) => onBowlerUpdate(index, "phone", e.target.value)}
          onBlur={(e) =>
            onBowlerBlur && onBowlerBlur(index, "phone", e.target.value)
          }
          error={errors[`bowler_${index}_phone`]}
          placeholder="Enter phone number"
          maxLength={20}
        />
      ) : null}

      {!showOptionalDetails && hiddenOptionalNames.length > 0 ? (
        <button
          type="button"
          onClick={() => setShowOptionalDetails(true)}
          className="text-sm text-brand-gold-ink hover:underline"
        >
          {addOptionalFieldsLabel(hiddenOptionalNames)}
        </button>
      ) : null}

      {/* Singles on a doubles form is a second team, not a side pot */}
      {showSinglesToggle && singlesEvent ? (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-brand-charcoal">
            Singles
          </label>
          <ToggleCard
            checked={Boolean(bowler.enterSingles)}
            onChange={() => onSinglesToggle && onSinglesToggle(index)}
            title={singlesEvent.eventName || "Singles"}
            disabled={!bowler.name.trim()}
            className="text-sm"
          />
        </div>
      ) : null}

      {/* Side Pot Selection */}
      {sidePotEvents.length > 0 && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-brand-charcoal">
            Side Pots
          </label>
          <SidePotSelection
            sidePotEvents={sidePotEvents}
            selectedSidePots={bowler.selectedSidePots || {}}
            onToggle={(eventId) => onSidePotToggle(index, eventId)}
            disabled={!bowler.name.trim()}
            allEventConfigs={allEventConfigs || []}
            allEventStatus={bowler.allEventStatus || null}
            selectedEvent={selectedEvent || null}
            getAllEventStatus={getAllEventStatus}
          />
        </div>
      )}

      <BowlerBracketTickets
        products={bracketProducts}
        counts={bowler.bracketTickets || {}}
        onChange={(productId, nextCount) =>
          onTicketChange && onTicketChange(index, productId, nextCount)
        }
        disabled={!bowler.name.trim()}
      />

      {/* Error Display */}
      {Object.keys(errors).some((key) =>
        key.startsWith(`bowler_${index}_`)
      ) && (
        <div className="text-sm text-red-600 space-y-1">
          {Object.entries(errors)
            .filter(([key]) => key.startsWith(`bowler_${index}_`))
            .map(([key, error]) => (
              <div key={key}>{error}</div>
            ))}
        </div>
      )}
    </div>
  );
};
